import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ProductTypeDetail,
  ProductTypePartWithStepMap,
  StepMapStep,
} from "@shared-core/data/manufacturing-setup-types";
import {
  formatPriceChecksUsedIn,
  formatSyncedFrom,
} from "@shared-core/domain/manufacturing-setup-presentation";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { EngineUseFieldTable } from "../app/components/domain/manufacturing/EngineUseFieldTable";
import { ManufacturingPressesConsideredTable } from "../app/components/domain/manufacturing/ManufacturingPressesConsideredTable";

type SubTab = "parts" | "step-map" | "presses-considered";

const SUB_TABS: { value: SubTab; label: string }[] = [
  { value: "parts", label: "Parts" },
  { value: "step-map", label: "Step map" },
  { value: "presses-considered", label: "Presses considered" },
];

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Live costing › Manufacturing setup, plus this product type's
 *  own identity once it has loaded. */
function crumbs(leaf?: string) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Live costing", to: paths.settingsLiveCostingStatus() },
  ];
  return leaf
    ? [
        ...base,
        {
          label: "Manufacturing setup",
          to: paths.settingsLiveCostingManufacturingSetup(),
        },
        { label: leaf },
      ]
    : [...base, { label: "Manufacturing setup" }];
}

export function ManufacturingSetupProductTypeDetailPage() {
  const adapter = useDataAdapter();
  const { productTypeId: productTypeIdParam } = useParams<{
    productTypeId: string;
  }>();
  const productTypeId = productTypeIdParam ? Number(productTypeIdParam) : NaN;
  const [searchParams, setSearchParams] = useSearchParams();
  const subTab = (searchParams.get("tab") as SubTab) || "parts";
  const activePartId = searchParams.get("part");

  const [detail, setDetail] = useState<ProductTypeDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (Number.isNaN(productTypeId)) return;
    setLoadError(false);
    adapter
      .getManufacturingProductType(productTypeId)
      .then((d) => {
        if (d === null) {
          setNotFound(true);
          return;
        }
        setDetail(d);
      })
      .catch(() => setLoadError(true));
  }, [adapter, productTypeId]);

  useEffect(() => {
    load();
  }, [load]);

  if (Number.isNaN(productTypeId) || notFound) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          No such product type. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (loadError) {
    return (
      <BreadcrumbsOnly>
        <p className="text-destructive">
          Couldn&rsquo;t load this product type. Please try again.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!detail) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">Loading...</p>
      </BreadcrumbsOnly>
    );
  }

  if (!detail.isReplicated) {
    return (
      <div className="space-y-4">
        <SettingsBreadcrumbs
          crumbs={crumbs(`${detail.productTypeId} · ${detail.name}`)}
        />
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-foreground opacity-60">
            Product type {detail.productTypeId} · {detail.name}
          </h1>
          <Badge variant="outline">Not yet replicated</Badge>
        </div>
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
          {detail.notReplicatedReason}
        </p>
      </div>
    );
  }

  const activePart: ProductTypePartWithStepMap =
    detail.parts.find(
      (p) => String(p.part.productTypePartId) === activePartId,
    ) ?? detail.parts[0];

  const setPartParam = (id: number) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("part", String(id));
        return params;
      },
      { replace: true },
    );

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={crumbs(`${detail.productTypeId} · ${detail.name}`)}
      />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          Product type {detail.productTypeId} · {detail.name}
        </h1>
        <p className="text-sm text-muted-foreground">
          Order expense {formatCurrencyGbp(detail.orderExpensePence / 100, 2)}
          {detail.markupMinPct != null && detail.markupMaxPct != null && (
            <>
              {" "}
              · markup {detail.markupMinPct}% / {detail.markupMaxPct}%
            </>
          )}
        </p>
        <p className="text-sm">
          {detail.priceChecksUsedIn === 0 ? (
            <span className="text-muted-foreground">
              {formatPriceChecksUsedIn(0)}
            </span>
          ) : (
            <Link
              to={paths.settingsLiveCostingChecks({
                view: "product-type",
                productTypeId: detail.productTypeId,
              })}
              className="text-[color:var(--color-primary)] hover:underline"
            >
              {formatPriceChecksUsedIn(detail.priceChecksUsedIn)}
            </Link>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatSyncedFrom(detail.sync)}
        </p>
      </div>

      <Tabs
        value={subTab}
        onValueChange={(v) =>
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              if (v === "parts") params.delete("tab");
              else params.set("tab", v);
              return params;
            },
            { replace: true },
          )
        }
      >
        <TabsList>
          {SUB_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {subTab === "parts" && (
        <div className="space-y-6">
          {detail.parts.map((p) => (
            <div key={p.part.productTypePartId} className="space-y-2">
              <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                {p.part.partName}
              </h2>
              <EngineUseFieldTable
                fields={p.part.fields}
                emptyMessage={`No fields captured for the ${p.part.partName} part.`}
              />
            </div>
          ))}
        </div>
      )}

      {subTab !== "parts" && (
        <PartPicker
          parts={detail.parts}
          activePartId={activePart.part.productTypePartId}
          onSelect={setPartParam}
        />
      )}

      {subTab === "step-map" && <StepMapTable steps={activePart.stepMap} />}
      {subTab === "presses-considered" && (
        <ManufacturingPressesConsideredTable
          exampleSpec={activePart.pressesConsideredExampleSpec}
          presses={activePart.pressesConsidered}
        />
      )}
    </div>
  );
}

function PartPicker({
  parts,
  activePartId,
  onSelect,
}: {
  parts: ProductTypePartWithStepMap[];
  activePartId: number;
  onSelect: (id: number) => void;
}) {
  if (parts.length <= 1) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {parts.map((p) => (
        <button
          key={p.part.productTypePartId}
          type="button"
          onClick={() => onSelect(p.part.productTypePartId)}
          className={`px-3 py-1 rounded-md text-sm transition-colors ${
            p.part.productTypePartId === activePartId
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {p.part.partName}
        </button>
      ))}
    </div>
  );
}

function StepMapTable({ steps }: { steps: StepMapStep[] }) {
  if (steps.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        No step map captured for this part.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">#</TableHead>
            <TableHead>Step</TableHead>
            <TableHead>Default</TableHead>
            <TableHead>Candidates</TableHead>
            <TableHead>Required</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...steps]
            .sort((a, b) => a.lineNumber - b.lineNumber)
            .map((step) => (
              <TableRow key={`${step.lineNumber}-${step.processTypeId}`}>
                <TableCell className="text-muted-foreground tabular-nums">
                  {step.lineNumber}
                </TableCell>
                <TableCell className="font-medium">
                  {step.processTypeName} ({step.processTypeId})
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {step.defaultCostCentre ? (
                    <Link
                      to={paths.settingsLiveCostingCostCentreDetail({
                        costCentreId: step.defaultCostCentre.costCentreId,
                      })}
                      className="text-[color:var(--color-primary)] hover:underline"
                    >
                      {step.defaultCostCentre.code}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {step.candidateCostCentres.length === 0
                    ? "—"
                    : step.candidateCostCentres.map((c) => c.code).join(", ")}
                  {step.autoSelectsCheapest && (
                    <Badge variant="neutral" className="ml-1.5">
                      Auto-selects cheapest
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {step.isRequired ? (
                    <Badge variant="success">Required</Badge>
                  ) : (
                    <span className="text-muted-foreground">Optional</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BreadcrumbsOnly({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs()} />
      {children}
    </div>
  );
}
